/** Server state for the batch tenant-config grid.
 *
 * Two queries — the combined runtime schema bundle (slow-moving, cached half
 * an hour) and every tenant's full masked config in one response — plus the
 * pure in-memory derivations (`buildConfigIndex` / `buildBatchRows`) the page
 * renders from. The derived rows/index deliberately do NOT depend on the
 * change-set state: pending edits overlay at the detail-panel level, so
 * editing never recomputes the whole grid.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import {
  getConfigSchemaBundle,
  listAllConfigs,
  type ConfigSchemaBundle,
  type TenantConfigItem,
  type TenantConfigList,
} from '@/api/tourOperatorConfigs';

import { buildBatchRows, buildConfigIndex, type BatchRow, type ConfigIndex } from './configIndex';

export interface BatchConfigData {
  schemaQuery: UseQueryResult<ConfigSchemaBundle>;
  configsQuery: UseQueryResult<TenantConfigList>;
  rows: BatchRow[];
  index: ConfigIndex;
  tenantById: Map<number, TenantConfigItem>;
  tenants: TenantConfigItem[];
  /** Refetch the bulk configs and resolve with the fresh list (undefined on
   *  failure) — the persist dialog's refresh-and-rebase entry point. */
  refetchConfigs: () => Promise<TenantConfigList | undefined>;
}

const NO_TENANTS: TenantConfigItem[] = [];

export function useBatchConfigData(): BatchConfigData {
  const schemaQuery = useQuery({
    queryKey: ['tourOperators', 'configSchemaBundle'],
    queryFn: getConfigSchemaBundle,
    staleTime: 30 * 60_000,
  });

  const configsQuery = useQuery({
    queryKey: ['tourOperators', 'configsBulk'],
    queryFn: listAllConfigs,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const tenants = configsQuery.data?.results ?? NO_TENANTS;

  const tenantById = useMemo(() => new Map(tenants.map((t) => [t.id, t])), [tenants]);

  const index = useMemo(() => buildConfigIndex(tenants), [tenants]);

  const bundle = schemaQuery.data;
  const rows = useMemo(
    () => (bundle ? buildBatchRows(bundle, index, tenants.length) : []),
    [bundle, index, tenants.length],
  );

  const { refetch } = configsQuery;
  const refetchConfigs = useCallback(async () => {
    const result = await refetch();
    return result.data;
  }, [refetch]);

  return { schemaQuery, configsQuery, rows, index, tenantById, tenants, refetchConfigs };
}
